import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { COMPANY, waLink } from "../data/site";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/about", label: "About Us", testid: "nav-link-about" },
  { to: "/products", label: "Products", testid: "nav-link-products" },
  { to: "/dealership", label: "Dealer & Distribution", testid: "nav-link-dealership" },
  { to: "/contact", label: "Contact Us", testid: "nav-link-contact" },
];

const linkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors ${
    isActive ? "text-[#1b4332] border-b-2 border-[#1b4332] pb-1" : "text-[#2d3748] hover:text-[#1b4332]"
  }`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[#e2ded4] transition-shadow duration-300 ${
        scrolled ? "shadow-[0_4px_20px_rgba(15,42,28,0.08)]" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img
              src="/logo.jpeg"
              alt="Organik Things logo"
              className="h-10 w-10 object-contain rounded-full"
            />
            <div className="leading-tight">
              <span className="font-heading font-bold text-[#1b4332] text-base sm:text-lg block">
                {COMPANY}
              </span>
              <span className="text-[11px] text-[#5a6b5c] tracking-wide">
                Est. 2021 · Indore, India
              </span>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-7" aria-label="Main navigation">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={l.testid} className={linkClass}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={waLink("Hello Organik Things, I would like to enquire about your eco-friendly products.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-whatsapp"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1b4332] border border-[#1b4332]/30 rounded-lg px-4 py-2 hover:bg-[#e8f5e9] transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <Link
              to="/dealership"
              data-testid="nav-cta-dealer"
              className="inline-flex items-center text-sm font-semibold text-white bg-[#1b4332] rounded-lg px-4 py-2 hover:bg-[#2d6a4f] transition-colors"
            >
              Become a Dealer
            </Link>
          </div>
          <button
            className="lg:hidden p-2 text-[#1b4332]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            aria-expanded={open}
            data-testid="nav-mobile-menu-button"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav
          className="lg:hidden border-t border-[#e2ded4] bg-white px-4 py-4 space-y-1"
          data-testid="nav-mobile-menu"
          aria-label="Mobile navigation"
        >
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`${l.testid}-mobile`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block py-2 text-sm font-medium ${isActive ? "text-[#1b4332]" : "text-[#2d3748]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/dealership"
            onClick={() => setOpen(false)}
            data-testid="nav-cta-dealer-mobile"
            className="block mt-2 text-center text-sm font-semibold text-white bg-[#1b4332] rounded-lg px-4 py-2.5"
          >
            Become a Dealer
          </Link>
        </nav>
      )}
    </header>
  );
};
